import { useCallback, useEffect, useRef, useState } from 'react'
import type { SectionView } from '@assessments/shared'
import { fetchSectionView } from './api.js'

export interface UseSectionView {
  data: SectionView | null
  loading: boolean
  error: string | null
  reload: () => void
}

/**
 * Loads the read-only section view for an org-survey id + section id. Refetches when
 * either id changes. A monotonic request id guards against an earlier in-flight
 * response applying after a newer load (or after unmount). A `reload` keeps returning
 * the last view while it runs or if it fails; a changed id drops it.
 */
export function useSectionView(organizationSurveyId: string, sectionId: string): UseSectionView {
  const key = `${organizationSurveyId}/${sectionId}`
  const [loaded, setLoaded] = useState<{ key: string; view: SectionView } | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const requestId = useRef(0)

  const load = useCallback(() => {
    const id = ++requestId.current
    setLoading(true)
    setError(null)
    void (async () => {
      try {
        const view = await fetchSectionView(organizationSurveyId, sectionId)
        if (id === requestId.current) setLoaded({ key, view })
      } catch (err: unknown) {
        if (id === requestId.current) setError(err instanceof Error ? err.message : String(err))
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    })()
    return () => {
      // Invalidate this request so a late response can't apply after cleanup.
      requestId.current++
    }
  }, [organizationSurveyId, sectionId, key])

  useEffect(() => load(), [load])

  const data = loaded?.key === key ? loaded.view : null
  return { data, loading, error, reload: load }
}
